import { CSSObject } from '@emotion/react'
import { Color, Spacing } from '@opallabs/gem'
import { WithRequiredTheme } from '@opallabs/lapidary'
import { fontSizes, fonts } from '@opallabs/pulsar'
import { colors } from '@opallabs/spectrum'
import type { CharacterCountPosition as Position } from '../types'

export const positions: {
  [Property in Position]: (offset: number) => CSSObject
} = {
  left: (offset) => ({
    top: '50%',
    transform: 'translateY(-50%)',
    left: -offset,
  }),
  right: (offset) => ({
    top: '50%',
    transform: 'translateY(-50%)',
    right: -offset,
  }),
  top: (offset) => ({
    left: '50%',
    transform: 'translateX(-50%)',
    top: -offset,
  }),
  bottom: (offset) => ({
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: -offset,
  }),
}

export const getCounterColor = (
  characterLimit: number,
  textLength: number,
  isSoftLimit: boolean
) => {
  if (textLength <= characterLimit - 10) {
    return colors.white.base
  }

  if (!isSoftLimit) {
    return textLength >= characterLimit ? colors.red.base : colors.orange.base
  }

  return colors.orange.base
}

export const characterCount = ({
  theme,
  position,
  offset,
  zIndex,
}: WithRequiredTheme & {
  position: Position
  offset: number
  zIndex: 'auto' | number
}): CSSObject => ({
  position: 'absolute',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  minInlineSize: '36px',
  blockSize: '32px',
  borderWidth: 0,
  borderRadius: 3,
  boxShadow: '0 0 6px rgba(0, 0, 0, 0.32)',
  fontSize: fontSizes.xxSmall,
  fontFamily: fonts.sansSerif,
  fontWeight: 600,
  lineHeight: '32px',
  paddingInline: Spacing.XS,
  color: Color[theme].Text.Default,
  zIndex,

  ...positions[position](offset),

  '&::before': {
    content: '""',
    position: 'absolute',
    inlineSize: 0,
    blockSize: 0,
    ...arrows[position],
  },
})

export const arrows: { [Property in Position]: CSSObject } = {
  left: {
    borderTop: '5px solid transparent',
    borderBottom: '5px solid transparent',
    borderLeft: '7px solid',
    borderLeftColor: 'inherit',
    top: '50%',
    right: '-5px',
    transform: 'translateY(-50%)',
  },
  right: {
    borderTop: '5px solid transparent',
    borderBottom: '5px solid transparent',
    borderRight: '7px solid',
    borderRightColor: 'inherit',
    top: '50%',
    left: '-5px',
    transform: 'translateY(-50%)',
  },
  top: {
    borderTop: '7px solid',
    borderTopColor: 'inherit',
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent',
    left: '50%',
    bottom: '-5px',
    transform: 'translateX(-50%)',
  },
  bottom: {
    borderBottom: '7px solid',
    borderBottomColor: 'inherit',
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent',
    left: '50%',
    top: '-5px',
    transform: 'translateX(-50%)',
  },
}
